'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Badge } from '@/components/ui/badge'

interface Dealer {
  id: string
  name: string
  location: string
  phone: string
  email: string
  lastContact?: string
  status: 'active' | 'inactive' | 'on-call'
  priority: 'high' | 'medium' | 'low'
}

interface Notification {
  id: string
  roomName: string
  dealerName: string
  dealerLocation: string
  reason: string
  currentEmotion: string
  timestamp: number
  dismissed: boolean
}

// Mock dealer data
const DEALERS: Dealer[] = [
  {
    id: 'D001',
    name: 'Rajesh Kumar',
    location: 'Mumbai, Maharashtra',
    phone: '+91 98765 43210',
    email: 'rajesh@example.com',
    lastContact: '2 days ago',
    status: 'active',
    priority: 'high',
  },
  {
    id: 'D002',
    name: 'Priya Sharma',
    location: 'Delhi, NCR',
    phone: '+91 98765 43211',
    email: 'priya@example.com',
    lastContact: '1 week ago',
    status: 'active',
    priority: 'medium',
  },
  {
    id: 'D003',
    name: 'Amit Patel',
    location: 'Pune, Maharashtra',
    phone: '+91 98765 43212',
    email: 'amit@example.com',
    lastContact: '3 hours ago',
    status: 'active',
    priority: 'high',
  },
  {
    id: 'D004',
    name: 'Sunita Reddy',
    location: 'Bangalore, Karnataka',
    phone: '+91 98765 43213',
    email: 'sunita@example.com',
    lastContact: '5 days ago',
    status: 'active',
    priority: 'medium',
  },
  {
    id: 'D005',
    name: 'Vikram Singh',
    location: 'Jaipur, Rajasthan',
    phone: '+91 98765 43214',
    email: 'vikram@example.com',
    lastContact: '1 day ago',
    status: 'active',
    priority: 'low',
  },
]

export default function TSOCallDashboard() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null)
  const [isInitiatingCall, setIsInitiatingCall] = useState(false)
  const [invitationLink, setInvitationLink] = useState('')
  const [showInvitationModal, setShowInvitationModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])

  // Load notifications
  useEffect(() => {
    loadNotifications()
    const interval = setInterval(loadNotifications, 5000) // Refresh every 5 seconds
    return () => clearInterval(interval)
  }, [])

  const loadNotifications = async () => {
    try {
      const response = await fetch('/api/video-call/tso/notifications')
      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications?.filter((n: Notification) => !n.dismissed) || [])
      }
    } catch (error) {
      console.error('Failed to load notifications:', error)
    }
  }

  const handleJoinEscalation = async (notification: Notification) => {
    try {
      const response = await fetch('/api/video-call/tso/join-room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: notification.roomName,
          tsoName: 'Territory Sales Officer',
        }),
      })

      if (response.ok) {
        const data = await response.json()
        const callUrl = `/video-call/tso-session?roomName=${notification.roomName}&token=${data.token}`
        window.location.href = callUrl
      }
    } catch (error) {
      console.error('Failed to join call:', error)
      alert('Failed to join call. Please try again.')
    }
  }

  const handleDismissNotification = async (notificationId: string) => {
    try {
      await fetch('/api/video-call/tso/dismiss-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      })
      loadNotifications()
    } catch (error) {
      console.error('Failed to dismiss notification:', error)
    }
  }

  const filteredDealers = DEALERS.filter(
    (dealer) =>
      dealer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.phone.includes(searchQuery)
  )

  const handleInitiateCall = async (dealer: Dealer) => {
    setSelectedDealer(dealer)
    setIsInitiatingCall(true)

    try {
      // Create room and invitation
      const response = await fetch('/api/video-call/tso/initiate-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealerId: dealer.id,
          dealerName: dealer.name,
          dealerLocation: dealer.location,
          dealerPhone: dealer.phone,
          dealerEmail: dealer.email,
          tsoName: 'Territory Sales Officer',
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to initiate call')
      }

      const data = await response.json()
      
      // Generate the full invitation URL
      const fullInvitationUrl = `${window.location.origin}${data.dealerInviteUrl}`
      
      // Show invitation modal with copy functionality
      setInvitationLink(fullInvitationUrl)
      setShowInvitationModal(true)
      setIsInitiatingCall(false)

      // Store room data for joining later
      sessionStorage.setItem('tsoCallData', JSON.stringify({
        roomName: data.roomName,
        token: data.tsoToken,
        dealerName: dealer.name,
      }))
      
    } catch (error) {
      console.error('Failed to initiate call:', error)
      alert('Failed to initiate call. Please try again.')
      setIsInitiatingCall(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleJoinCall = () => {
    const callData = sessionStorage.getItem('tsoCallData')
    if (callData) {
      const { roomName, token } = JSON.parse(callData)
      const callUrl = `/video-call/tso-session?roomName=${roomName}&token=${token}`
      window.location.href = callUrl
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive'
      case 'medium': return 'default'
      case 'low': return 'secondary'
      default: return 'default'
    }
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="border-b bg-white dark:bg-gray-800">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                TSO Call Dashboard
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Initiate video calls with dealers - AI assistant (Priya) will handle the conversation
              </p>
            </div>
            <Badge variant="default" className="gap-2">
              <Icons.video className="h-4 w-4" />
              {DEALERS.filter(d => d.status === 'active').length} Active Dealers
            </Badge>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Icons.search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search dealers by name, location, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-cornflower focus:outline-none focus:ring-1 focus:ring-brand-cornflower dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Escalation Notifications */}
      {notifications.length > 0 && (
        <div className="border-b bg-red-50 dark:bg-red-900/20">
          <div className="px-6 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Icons.alertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                🚨 Urgent: Dealer Requests ({notifications.length})
              </h3>
            </div>
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="flex items-center justify-between rounded-lg border border-red-200 bg-white p-4 shadow-sm dark:border-red-800 dark:bg-gray-800"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
                      <Icons.user className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {notification.dealerName}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {notification.dealerLocation} • {notification.reason}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(notification.timestamp).toLocaleTimeString()} • Room: {notification.roomName}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleJoinEscalation(notification)}
                      className="gap-2 bg-red-600 hover:bg-red-700"
                    >
                      <Icons.video className="h-4 w-4" />
                      Join Call Now
                    </Button>
                    <Button
                      onClick={() => handleDismissNotification(notification.id)}
                      variant="outline"
                      size="icon"
                    >
                      <Icons.close className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dealer List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDealers.map((dealer) => (
            <div
              key={dealer.id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
            >
              {/* Dealer Header */}
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-cornflower/10">
                    <Icons.user className="h-6 w-6 text-brand-cornflower" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {dealer.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {dealer.id}
                    </p>
                  </div>
                </div>
                <Badge variant={getPriorityColor(dealer.priority)}>
                  {dealer.priority}
                </Badge>
              </div>

              {/* Dealer Info */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Icons.building className="h-4 w-4 text-gray-400" />
                  <span>{dealer.location}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Icons.mail className="h-4 w-4 text-gray-400" />
                  <span className="truncate">{dealer.phone}</span>
                </div>
                {dealer.lastContact && (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Icons.clock className="h-4 w-4 text-gray-400" />
                    <span>Last contact: {dealer.lastContact}</span>
                  </div>
                )}
              </div>

              {/* Call Button */}
              <div className="mt-4">
                <Button
                  onClick={() => handleInitiateCall(dealer)}
                  disabled={isInitiatingCall && selectedDealer?.id === dealer.id}
                  className="w-full gap-2"
                  size="sm"
                >
                  {isInitiatingCall && selectedDealer?.id === dealer.id ? (
                    <>
                      <Icons.loader className="h-4 w-4 animate-spin" />
                      Initiating Call...
                    </>
                  ) : (
                    <>
                      <Icons.video className="h-4 w-4" />
                      Call with AI Assistant
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredDealers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icons.user className="mb-4 h-12 w-12 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              No dealers found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your search query
            </p>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="border-t bg-blue-50 px-6 py-3 dark:bg-blue-900/20">
        <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
          <Icons.info className="h-4 w-4" />
          <span>
            When you initiate a call, Priya (AI Assistant) will handle the conversation with the dealer. 
            You can join or monitor the call anytime from the TSO Dashboard.
          </span>
        </div>
      </div>

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white p-6 shadow-2xl dark:bg-gray-800">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
                  <Icons.checkCircle className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Call Invitation Created!
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    For {selectedDealer?.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowInvitationModal(false)}
                className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Icons.close className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Invitation Link */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Invitation Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitationLink}
                  readOnly
                  className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <Button
                  onClick={handleCopyLink}
                  size="lg"
                  variant={copied ? 'default' : 'outline'}
                  className="gap-2"
                >
                  {copied ? (
                    <>
                      <Icons.check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Icons.copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Share this link with {selectedDealer?.name} via WhatsApp, Email, or SMS
              </p>
            </div>

            {/* Instructions */}
            <div className="mb-6 space-y-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                  1
                </div>
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Copy and send</strong> the invitation link to the dealer
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                  2
                </div>
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Priya (AI)</strong> will automatically greet and engage with the dealer
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                  3
                </div>
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>You can join</strong> the call anytime to take over or monitor
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleJoinCall}
                className="flex-1 gap-2"
                size="lg"
              >
                <Icons.video className="h-5 w-5" />
                Join Call Now
              </Button>
              <Button
                onClick={() => setShowInvitationModal(false)}
                variant="outline"
                size="lg"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
